import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RevealWrapper from '@/pages/home/components/RevealWrapper';

type FormState = 'idle' | 'loading' | 'success' | 'error';
const NOTIFY_ENDPOINT = '/api/notify';

export default function NotifyBlock() {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<FormState>('idle');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [errorText, setErrorText] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const validateEmail = (val: string) => {
    if (!val.trim()) return t('kg.notify.err.required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return t('kg.notify.err.invalid');
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }
    setEmailError('');
    setErrorText('');
    setFormState('loading');
    try {
      const payload = {
        email,
        project: 'Kozmik Günlük',
        source: 'kozmik-gunluk-notify',
      };

      const res = await fetch(NOTIFY_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let result: { success?: boolean | string; message?: string } | null = null;
      const responseType = res.headers.get('content-type') ?? '';
      if (responseType.includes('application/json')) {
        result = await res.json().catch(() => null) as { success?: boolean | string; message?: string } | null;
      } else {
        const text = await res.text().catch(() => '');
        if (text) {
          try {
            result = JSON.parse(text) as { success?: boolean | string; message?: string };
          } catch {
            result = { message: text };
          }
        }
      }

      if (!res.ok) {
        console.error('[KozmikNotify] Notify API request failed', { status: res.status, result });
        setErrorText(t('kg.notify.error'));
        setFormState('error');
        return;
      }

      const success = result?.success === true || result?.success === 'true' || result?.success === undefined;
      if (success) {
        setFormState('success');
        setEmail('');
      } else {
        console.error('[KozmikNotify] Notify API returned unsuccessful response', result);
        setErrorText(t('kg.notify.error'));
        setFormState('error');
      }
    } catch {
      setErrorText(t('kg.notify.error'));
      setFormState('error');
    }
  };

  const stats = [
    { valueKey: 'kg.notify.stat.launchVal',   labelKey: 'kg.notify.stat.launch' },
    { valueKey: 'kg.notify.stat.platformVal', labelKey: 'kg.notify.stat.platform' },
    { valueKey: 'kg.notify.stat.priceVal',    labelKey: 'kg.notify.stat.price' },
  ];

  return (
    <section id="notify" className="bg-[#0c0c0c] border-t border-purple-500/10 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-purple-900/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="px-6 lg:px-14 py-8 border-b border-purple-500/10 relative z-10">
        <span className="font-mono text-xs text-purple-400/60 tracking-widest uppercase">{t('kg.notify.label')}</span>
      </div>

      <div className="px-6 lg:px-14 py-20 lg:py-32 relative z-10">
        <RevealWrapper direction="up" threshold={0.15}>
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 flex items-center justify-center border border-purple-500/20 bg-purple-500/5 mx-auto mb-10">
              <i className="ri-rocket-2-line text-2xl text-purple-400" />
            </div>

            <p className="font-mono text-xs text-purple-400/50 tracking-widest uppercase mb-6">{t('kg.notify.sublabel')}</p>
            <h2 className="text-3xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-6">
              {t('kg.notify.title')}<br />
              <span className="text-purple-400">{t('kg.notify.title.accent')}</span>
            </h2>
            <p className="text-neutral-500 text-sm leading-loose mb-14 max-w-md mx-auto">
              {t('kg.notify.desc')}
            </p>

            {formState === 'success' ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-16 h-16 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping" />
                  <div className="relative w-16 h-16 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-purple-400" />
                  </div>
                </div>
                <p className="font-mono text-sm font-bold text-purple-300 tracking-widest uppercase">{t('kg.notify.success.title')}</p>
                <p className="text-neutral-600 text-sm">{t('kg.notify.success.desc')}</p>
                <button
                  onClick={() => setFormState('idle')}
                  className="mt-2 font-mono text-xs text-purple-400/50 hover:text-purple-400 tracking-widest uppercase transition-colors duration-200 cursor-pointer"
                >
                  {t('kg.notify.success.another')}
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id="kozmik-notify-form"
                data-readdy-form
                onSubmit={handleSubmit}
                noValidate
                className="w-full"
              >
                <div className="flex flex-col sm:flex-row gap-0 border border-purple-500/20 bg-[#111] overflow-hidden max-w-lg mx-auto">
                  <div className="flex-1 relative">
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                      onBlur={() => setEmailError(validateEmail(email))}
                      placeholder={t('kg.notify.placeholder')}
                      className={`w-full bg-transparent px-5 py-4 font-mono text-sm text-white placeholder-neutral-700 outline-none border-0 ${emailError ? 'border-b-2 border-red-500/60' : ''}`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formState === 'loading'}
                    className="px-7 py-4 bg-purple-600/20 hover:bg-purple-600/30 border-t sm:border-t-0 sm:border-l border-purple-500/20 text-purple-300 font-mono text-xs tracking-widest uppercase transition-all duration-200 sm:whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formState === 'loading' ? (
                      <><i className="ri-loader-4-line animate-spin text-sm" />{t('kg.notify.sending')}</>
                    ) : (
                      <><i className="ri-notification-3-line text-sm" />{t('kg.notify.btn')}</>
                    )}
                  </button>
                </div>

                {emailError && (
                  <div className="flex items-center gap-1.5 mt-2 max-w-lg mx-auto">
                    <i className="ri-error-warning-fill text-red-400 text-xs" />
                    <span className="font-mono text-xs text-red-400">{emailError}</span>
                  </div>
                )}

                {formState === 'error' && (
                  <div className="flex items-center justify-center gap-1.5 mt-3">
                    <i className="ri-error-warning-fill text-red-400 text-xs" />
                    <span className="font-mono text-xs text-red-400">{errorText || t('kg.notify.error')}</span>
                  </div>
                )}

                <p className="font-mono text-[10px] text-neutral-700 tracking-widest mt-4">
                  {t('kg.notify.footer')}
                </p>
              </form>
            )}
          </div>
        </RevealWrapper>

        <RevealWrapper direction="up" delay={150} threshold={0.1}>
          <div className="mt-24 grid grid-cols-3 gap-px border border-purple-500/10 max-w-lg mx-auto overflow-hidden">
            {stats.map((s) => (
              <div key={s.labelKey} className="bg-[#0f0f0f] px-6 py-7 text-center">
                <p className="font-mono text-base font-black text-white mb-2">{t(s.valueKey)}</p>
                <p className="font-mono text-[10px] text-purple-400/40 tracking-widest uppercase">{t(s.labelKey)}</p>
              </div>
            ))}
          </div>
        </RevealWrapper>
      </div>
    </section>
  );
}

